import { Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  Activity, BarChart3, Beaker, CalendarDays, CandlestickChart, ChevronLeft, Database, History,
  GitCompare, LayoutDashboard, Menu, Microscope, Network, RadioTower, Settings, ShieldAlert, ShieldCheck, Sparkles, TrendingUp,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import PaperExecutionNotifier from './PaperExecutionNotifier'
import { Loading } from './UI'

type NavGroup = {
  label: string
  items: { to: string; label: string; icon: LucideIcon }[]
}

const navGroups: NavGroup[] = [
  {
    label: 'Esplora',
    items: [
      { to: '/', label: 'Panoramica', icon: LayoutDashboard },
      { to: '/markets', label: 'Analisi mercati', icon: BarChart3 },
      { to: '/correlations', label: 'Correlazioni', icon: Network },
      { to: '/equity-lab', label: 'Laboratorio azionario', icon: CandlestickChart },
      { to: '/regime', label: 'Regime macro', icon: Activity },
      { to: '/calendar', label: 'Calendario', icon: CalendarDays },
    ],
  },
  {
    label: 'Ricerca',
    items: [
      { to: '/strategies', label: 'Strategie', icon: CandlestickChart },
      { to: '/backtest', label: 'Backtest', icon: TrendingUp },
      { to: '/research', label: 'Ricerca controllata', icon: Microscope },
      { to: '/compare', label: 'Confronti', icon: GitCompare },
      { to: '/experiments', label: 'Archivio esperimenti', icon: Beaker },
    ],
  },
  {
    label: 'Operatività',
    items: [
      { to: '/operations', label: 'Operatività paper', icon: RadioTower },
      { to: '/operations/history', label: 'Operatività passata', icon: History },
      { to: '/operations/shadow', label: 'Simulatore shadow', icon: GitCompare },
      { to: '/operations/real', label: 'Operatività reale', icon: ShieldAlert },
      { to: '/operations/real-readiness', label: 'Preparazione reale', icon: ShieldCheck },
      { to: '/signal', label: 'Segnale serale', icon: Sparkles },
    ],
  },
  {
    label: 'Sistema',
    items: [
      { to: '/data', label: 'Dati e fonti', icon: Database },
      { to: '/settings', label: 'Impostazioni', icon: Settings },
    ],
  },
]

const navItems = navGroups.flatMap(group => group.items)

function labelForPath(pathname: string) {
  if (pathname.startsWith('/strategies/')) return 'Dettaglio strategia'
  if (pathname.startsWith('/research/campaign/')) return 'Campagna di ricerca'
  return navItems.find(item => item.to === '/' ? pathname === '/' : pathname === item.to)?.label || 'Pagina non trovata'
}

export default function Layout() {
  const [open, setOpen] = useState(false)
  const { pathname } = useLocation()
  const pageLabel = labelForPath(pathname)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const mainRef = useRef<HTMLElement>(null)
  const initialRoute = useRef(true)

  const closeNavigation = useCallback((restoreFocus = false) => {
    setOpen(false)
    if (restoreFocus) window.requestAnimationFrame(() => menuButtonRef.current?.focus())
  }, [])

  useEffect(() => {
    document.body.classList.toggle('sidebar-open', open)
    if (open) window.requestAnimationFrame(() => closeButtonRef.current?.focus())
    return () => document.body.classList.remove('sidebar-open')
  }, [open])

  useEffect(() => {
    if (!open) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeNavigation(true)
      if (event.key === 'Tab') {
        const sidebar = document.getElementById('primary-sidebar')
        const focusable = Array.from(sidebar?.querySelectorAll<HTMLElement>('a[href],button:not([disabled])') || [])
          .filter(element => element.offsetParent !== null)
        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (!first || !last) return
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        } else if (!sidebar?.contains(document.activeElement)) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [closeNavigation, open])

  useEffect(() => {
    setOpen(false)
    window.scrollTo(0, 0)
    document.title = pageLabel === 'Panoramica' ? 'Strategy Lab' : `${pageLabel} · Strategy Lab`
    if (initialRoute.current) {
      initialRoute.current = false
      return
    }
    window.requestAnimationFrame(() => mainRef.current?.focus({ preventScroll: true }))
  }, [pageLabel, pathname])

  return <div className="app-shell">
    <a className="skip-link" href="#main-content">Vai al contenuto</a>
    <aside id="primary-sidebar" className={`sidebar ${open ? 'open' : ''}`} aria-label="Navigazione dell’applicazione" role={open ? 'dialog' : undefined} aria-modal={open ? true : undefined}>
      <div className="brand">
        <div className="brand-mark"><BarChart3 size={22} aria-hidden="true" /></div>
        <div><strong>Strategy Lab</strong><span>Multi-strumento</span></div>
        <button ref={closeButtonRef} type="button" className="sidebar-close" onClick={() => closeNavigation(true)} aria-label="Chiudi navigazione">
          <ChevronLeft aria-hidden="true" />
        </button>
      </div>
      <nav aria-label="Navigazione principale">
        {navGroups.map(group => <div className="nav-group" key={group.label}>
          <span className="nav-group-label">{group.label}</span>
          {group.items.map(({ to, label, icon: Icon }) =>
            <NavLink key={to} to={to} end={to === '/' || to.startsWith('/operations')} onClick={() => closeNavigation()}>
              <Icon size={18} aria-hidden="true" /><span>{label}</span>
            </NavLink>
          )}
        </div>)}
      </nav>
      <div className="sidebar-note">
        <span className="safe-dot" />
        <div><strong>Laboratorio locale</strong><small>Nessun ordine live · paper separato</small></div>
      </div>
    </aside>
    {open && <button type="button" className="sidebar-scrim" onClick={() => closeNavigation(true)} aria-label="Chiudi navigazione" />}
    <main ref={mainRef} id="main-content" className="main-area" tabIndex={-1} inert={open ? true : undefined}>
      <PaperExecutionNotifier />
      <header className="mobile-header">
        <button
          ref={menuButtonRef}
          type="button"
          className="mobile-menu-button"
          onClick={() => setOpen(true)}
          aria-label="Apri navigazione"
          aria-expanded={open}
          aria-controls="primary-sidebar"
        >
          <Menu aria-hidden="true" />
        </button>
        <div className="mobile-context"><span>Strategy Lab</span><strong>{pageLabel}</strong></div>
      </header>
      <Suspense fallback={<div className="page route-page-loading"><Loading label="Carico la vista…" /></div>}>
        <Outlet />
      </Suspense>
    </main>
  </div>
}
